import { IconArrowRight, ImageButton } from '@gmg/gmg-react-components';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import useFormatter from '../../customHooks/useFormatter';
import { SiteListItem } from '../../graphql/useGetOrganizationSites';
import { organizationPaths } from '../../routes';
import WrappedSpinner from '../../shared/WrappedSpinner';
import { Table, TableContainer, TableContainerCardStyle, Thead, Tr } from '../../shared/TableStyle';

interface SitesListProps {
    sites: Array<SiteListItem>;
    isLoading: boolean;
}

const SitesList: FC<SitesListProps> = ({ sites, isLoading }) => {
    const { t } = useTranslation();
    const { formatDate } = useFormatter();
    const navigate = useNavigate();

    if (isLoading) {
        return <WrappedSpinner label={t('Common.Spinner', 'Loading...')} labelPosition="bottom" />;
    }

    return (
        <TableContainer>
            <TableContainerCardStyle aria-label="TableContainerCardStyle" className="vertical-scrollbar">
                <Table aria-label="List of Sites">
                    <Thead>
                        <Tr isSelected={false} isHeaderRow>
                            <th className="px12_600_normal">
                                {t('SitesList.tableLabels.site_title', {
                                    defaultValue: 'Site (total number: {{totalNumber}})',
                                    totalNumber: sites.length || '0',
                                })}
                            </th>
                            <th className="px12_600_normal">{t('SitesList.tableLabels.city_title', 'City')}</th>
                            <th className="px12_600_normal">{t('SitesList.tableLabels.country_title', 'Country')}</th>
                            <th className="px12_600_normal">{t('SitesList.tableLabels.organizationCode_title', 'Organization code')}</th>
                            <th className="px12_600_normal">{t('SitesList.tableLabels.registrationDate_title', 'Registration date')}</th>
                            <th className="px12_600_normal">{t('SitesList.tableLabels.contact_title', 'Primary contact')}</th>
                            <th></th>{/* in order to fill the last item's place in the thead 'cos we have an extra td for action button */}
                        </Tr>
                    </Thead>
                    <tbody>
                        {sites.map((site) => (
                            <Tr
                                isSelected={false}
                                key={site.id}
                                onClick={() => navigate(organizationPaths.root(site.id))}
                                isClickable
                                data-testid="row"
                            >
                                <td className="px12_400_normal">{site.name}</td>
                                <td className="px12_400_normal">{site.city}</td>
                                <td className="px12_400_normal">{site.country}</td>
                                <td className="px12_400_normal">{site.code}</td>
                                <td className="px12_400_normal">{formatDate(site.registration)}</td>
                                <td className="px12_400_normal">
                                    <div>{site.contactPerson}</div>
                                    <div className="px10_400_weak">{site.contactEmail}</div>
                                </td>
                                <td className="actions">
                                    <ImageButton
                                        icon={<IconArrowRight />}
                                        aria-label="Open File icon"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            navigate(organizationPaths.root(site.id));
                                        }}
                                    />
                                </td>
                            </Tr>
                        ))}
                    </tbody>
                </Table>
            </TableContainerCardStyle>
        </TableContainer>
    );
};

export default SitesList;
